import { Box, BoxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronBack } from '@styled-icons/ionicons-sharp';
import { usePreviousPathname } from 'Contexts/TrackNavigationEvents';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import React, { FC, PropsWithChildren, ReactElement, useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { PAGE_TITLE_FROM_ROUTE, sanitizePathname } from 'utils/pageTitle.utils';
import BreadCrumbs from './BreadCrumbs';

const StyledBackLink = styled(NavLink)(({ theme }) => ({
  display: 'block',
  color: theme.custom.colors.almostBlack,
  textDecoration: 'none',
  transition: theme.custom.defaultTransition('color'),
  fontSize: 14,
  fontWeight: 400,
  maxWidth: 'fit-content', // Bug: Prevents the link from taking the full width of the container and being clickable outside of the text
  '&:hover': {
    color: theme.custom.colors.actionButtonHover,
  },
}));

const StyledCrumbs = styled(BreadCrumbs)({
  position: 'absolute',
  top: 0,
  transform: 'translateY(-110%)',

  '& .MuiBreadcrumbs-ol': {
    flexWrap: 'nowrap',
  },

  '& a': {
    whiteSpace: 'nowrap',
  },
});

const StyledThemeButton = styled(ThemeButton)(({ theme }) => ({
  background: theme.custom.themeColors.accent[40],
  color: theme.custom.themeColors.grayScale[100],
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
  letterSpacing: '-0.28px',
  '&:hover': {
    background: theme.custom.themeColors.accent[60],
  },
}));

const GoBackLabel = styled('span')({
  verticalAlign: 'middle',
});

type IPageTitleProps = {
  title: string;
  titleExtension?: string;
  breadcrumbs?: { label: string; to: string }[];
  className?: string;
  goBackLabel?: string;
  TitleAdornment?: ReactElement;
  goBackExtension?: ReactElement;
  goBackUrl?: string;
  enableAutoGoBackHistoryButton?: boolean;
};

const PageTitle: FC<PropsWithChildren<IPageTitleProps> & BoxProps> = ({
  title,
  titleExtension,
  breadcrumbs,
  className,
  children,
  TitleAdornment,
  goBackLabel,
  goBackExtension,
  goBackUrl,
  enableAutoGoBackHistoryButton,
  ...props
}) => {
  const { previousPathname, currentPathname } = usePreviousPathname();
  const navigate = useNavigate();

  const goBackTitle = useMemo(() => {
    const sanitizedPreviousPathname = sanitizePathname(previousPathname);

    if (!sanitizedPreviousPathname) {
      return null;
    }

    // Extract UUIDs from both paths to compare
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
    const previousIds = previousPathname?.match(uuidRegex) || [];
    const currentIds = currentPathname?.match(uuidRegex) || [];

    // If IDs are present in both paths, ensure they're different to not show the go back button when navigating between two subsections in the same library item
    if (previousIds.length > 0 && currentIds.length > 0) {
      const hasSameIds = previousIds.every((id, index) => id === currentIds[index]);
      if (hasSameIds) {
        return null;
      }
    }

    return PAGE_TITLE_FROM_ROUTE[sanitizedPreviousPathname];
  }, [previousPathname, currentPathname]);

  return (
    <Box mb={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        position="relative"
        className={className}
        mt={1}
        {...props}
      >
        {breadcrumbs && <StyledCrumbs breadcrumbs={breadcrumbs} />}
        <Box display="flex" alignItems="center">
          <ThemeTypography variant="TITLE_MEDIUM" data-testid="page-title">
            {title}
          </ThemeTypography>
          {titleExtension && (
            <Box ml={2}>
              <ThemeTypography
                variant="TITLE_MEDIUM"
                color="GRAY"
                data-testid="page-title-extension"
              >
                - {titleExtension}
              </ThemeTypography>
            </Box>
          )}
          {TitleAdornment}
        </Box>
        {children}
      </Box>
      <Box display="flex" alignItems="center" gap={2} mt={1}>
        {goBackUrl && (
          <StyledBackLink to={goBackUrl ? goBackUrl : ''} data-testid="navigate-back-link">
            <ChevronBack size={16} data-tut="onboarding_tour__exit" />
            {<GoBackLabel>{goBackLabel || 'Go back'}</GoBackLabel>}
          </StyledBackLink>
        )}

        {enableAutoGoBackHistoryButton && previousPathname && goBackTitle && (
          <StyledThemeButton
            onClick={() => navigate(previousPathname)}
            startIcon={<Icon name="jump-back" color="gray-100" />}
            typographyColor="ALMOST_BLACK"
          >
            {goBackTitle}
          </StyledThemeButton>
        )}

        {goBackExtension && <Box>{goBackExtension}</Box>}
      </Box>
    </Box>
  );
};

export default PageTitle;
